import rtlPlugin from "stylis-plugin-rtl";
import { useState, useEffect } from "react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import { styled } from "@mui/material";
import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import authStore from "@/stores/authStore";
import { OrderStatus, AccountingDocType } from "@/models/enums";
import AccountingDetailsFilter from "@/components/accounting/accountingDetailsFilter";
import popupService from "@/services/popupService";
import api from "@/services/api";
import "./accountingStyles.css";
import { AccountingDetailsFilterRequest, InvoiceDetails, InvoiceDocumentOrder } from "@/models/accounting";
import ArrowElement from "@/assets/images/ArrowElement.svg";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDate, formatAmount, checkPermissionsWithAlert, checkPermissionsWithoutAlert } from "@/util";
import { PermissionName } from "@/models/apiModels";
import AccountingInvoiceTab from "./accountingInvoiceTab";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const BackToDocsBtn = styled(ButtonUnstyled)(
    () => `
          &:hover {
            background-color: #222C4B;
          }
        
          &.${buttonUnstyledClasses.active} {
            background-color: #222C4B;
          }

          &.${buttonUnstyledClasses.root} {
            height: 40px;
            font-size: 16px;
            display: flex;
            align-items: center;
            gap: 7px;
          }
        
          &.${buttonUnstyledClasses.focusVisible} {
            box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
            outline: none;
          }
        
          &.${buttonUnstyledClasses.disabled} {
            opacity: 0.5;
            cursor: not-allowed;
          }`
);

const DetailsBtn = styled(ButtonUnstyled)(
    () => `
          &:hover {
            background-color: #222C4B;
            width: 100%;
          }
        
          &.${buttonUnstyledClasses.active} {
            background-color: #222C4B;
            width: 100%;
          }

          &.${buttonUnstyledClasses.root} {
            width: 100%;
            height: 26px;
            font-size: 14px;
          }
        
          &.${buttonUnstyledClasses.focusVisible} {
            box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
            outline: none;
            width: 100%;
          }
        
          &.${buttonUnstyledClasses.disabled} {
            opacity: 0.5;
            cursor: not-allowed;
            width: 100%;
          }`
);

const AccountingDocsInvoice = () => {
    const [invoice, setInvoice] = useState<InvoiceDetails>();
    const [accountingFilter, setAccountingFilter] = useState<AccountingDetailsFilterRequest>(
        new AccountingDetailsFilterRequest()
    );
    const [showAccountingTable, setShowAccountingTable] = useState(true);
    const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDocumentOrder | null>(null);

    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams();

    const onSearchClick = async () => {
        await setShowAccountingTable(false);
        startFetching();
        setShowAccountingTable(true);
    };

    let startFetchingBusy = false;
    const startFetching = async () => {
        if (!checkPermissionsWithAlert(PermissionName.ListOfDocuments)) {
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            accountingFilter.invoiceDocumentId = Number.parseInt(id ?? "");
            popupService.showLoading();
            const response = await api.getInvoiceDetails(accountingFilter);
            setInvoice(response.data);
            popupService.close();
        } catch (err) {
            setInvoice(undefined);
            await popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    };

    async function sortBy(sortBy: string) {
        if (accountingFilter.orderBy !== sortBy) {
            setAccountingFilter((prevData) => ({
                ...prevData,
                orderAsc: true,
                orderBy: sortBy,
            }));
        } else {
            setAccountingFilter((prevData) => ({
                ...prevData,
                orderAsc: !accountingFilter.orderAsc,
                orderBy: sortBy,
            }));
        }
    }

    function getArrowClass(sort: string) {
        if (sort === accountingFilter.orderBy) {
            if (accountingFilter.orderAsc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    function navigateToOrderDetails(order: InvoiceDocumentOrder) {
        navigate(`/accounting/invoice/${invoice?.baseAccountingDocumentId}/${order.id}`, {
            state: { docketNumber: order.docket },
        });
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [accountingFilter.orderBy, accountingFilter.orderAsc]);

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            {checkPermissionsWithoutAlert(PermissionName.ListOfDocuments) ? (
                <div>
                    <div className="accounting-details-header">
                        {selectedInvoice ? (
                            <AccountingInvoiceTab selectedInvoice={selectedInvoice} invoiceDetails={invoice} />
                        ) : (
                            <div className="doc-info-container">
                                <div className="doc-info-column">
                                    <div className="doc-info-row">
                                        <div className="doc-info-label">{t("accounting.documentNumber")}:</div>
                                    </div>
                                    <div className="doc-info-row">
                                        <div className="doc-info-label">{t("accounting.connectedOrdersQTY")}:</div>
                                    </div>
                                </div>
                                <div className="doc-info-column">
                                    <div className="doc-info-row">
                                        <div className="doc-info-value">{invoice?.documentNumber}</div>
                                    </div>
                                    <div className="doc-info-row">
                                        <div className="doc-info-value">{invoice?.connectedOrdersQty}</div>
                                    </div>
                                </div>
                                <div className="doc-info-column">
                                    <div className="doc-info-row">
                                        <div className="doc-info-label">{t("accounting.documentType")}:</div>
                                    </div>
                                    <div className="doc-info-row">
                                        <div className="doc-info-label">{t("accounting.additionalSum")}:</div>
                                    </div>
                                </div>
                                <div className="doc-info-column">
                                    <div className="doc-info-row">
                                        <div className="doc-info-value">
                                            {t(`enums.AccountingDocType.${invoice?.docType}`)}
                                        </div>
                                    </div>
                                    <div className="doc-info-row">
                                        <div className="doc-info-value">
                                            {formatAmount(invoice?.extraTotalAmount, invoice?.currencyId)}
                                        </div>
                                    </div>
                                </div>
                                <div className="doc-info-column">
                                    <div className="doc-info-row">
                                        <div className="doc-info-label">{t("accounting.docAmount")}:</div>
                                    </div>
                                </div>
                                <div className="doc-info-column">
                                    <div className="doc-info-row">
                                        <div className="doc-info-value">
                                            {formatAmount(invoice?.total, invoice?.currencyId)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {selectedInvoice ? (
                            <div className="back-to-container">
                                <BackToDocsBtn className="basic-button" onClick={() => setSelectedInvoice(null)}>
                                    <img className="back-to-list-arrow" src={ArrowElement} alt="ArrowElement" />
                                    {t("accounting.button.backToAccountings")}
                                </BackToDocsBtn>
                            </div>
                        ) : (
                            <div className="back-to-container">
                                <BackToDocsBtn className="basic-button" onClick={() => navigate("/accounting")}>
                                    <img className="back-to-list-arrow" src={ArrowElement} alt="ArrowElement" />
                                    {t("accounting.button.backToAccountings")}
                                </BackToDocsBtn>
                            </div>
                        )}
                    </div>
                    <div className="accounting-container">
                        <AccountingDetailsFilter
                            searchClick={onSearchClick}
                            filter={accountingFilter}
                            setFilter={setAccountingFilter}
                            docType={AccountingDocType.Invoice}
                        />
                        {showAccountingTable ? (
                            <div id="orders-list-grid" className="employee-list-grid">
                                <table>
                                    <thead id="orders-table-header">
                                        <tr>
                                            <td>{t("orders.externalSystem")}</td>
                                            <td>
                                                <div className="table-number-format-name">
                                                    {t("accounting.docAmount")}
                                                </div>
                                            </td>
                                            <td onClick={() => sortBy("OrderNumber")}>
                                                <div className="table-number-format-name">
                                                    {t("orders.orderNumber")}
                                                    <i className={`icon-f ${getArrowClass("OrderNumber")} fs-12`} />
                                                </div>
                                            </td>
                                            <td>{t("orders.orderStatus")}</td>
                                            <td>{t("orders.orderType")}</td>
                                            <td onClick={() => sortBy("CreationDate")}>
                                                <div className="table-number-format-name">
                                                    {t("orders.creationDate")}
                                                    <i className={`icon-f ${getArrowClass("CreationDate")} fs-12`} />
                                                </div>
                                            </td>
                                            <td>
                                                <div className="table-number-format-name">{t("orders.orderTotal")}</div>
                                            </td>
                                            <td className="cursor-pointer">
                                                <span>{t("orders.employeeName")}</span>
                                            </td>
                                            <td>
                                                <div className="table-number-format-name">
                                                    {t("orders.paidByDigitalWallet")}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="table-number-format-name">
                                                    {t("orders.paidByEmployee")}
                                                </div>
                                            </td>
                                            <td>{t("orders.details")}</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoice?.orders?.map((order) => (
                                            <tr key={order.id} onClick={() => setSelectedInvoice(order)}>
                                                <td>{order.externalSystem}</td>
                                                <td>
                                                    <div className="table-number-format-value">
                                                        {formatAmount(order.amount, order.currencyId)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-number-format-value">{order.docket}</div>
                                                </td>
                                                <td>
                                                    <div className="employee-status">
                                                        <div
                                                            className={
                                                                order.status.toString() === OrderStatus[OrderStatus.OK]
                                                                    ? "status-color-active"
                                                                    : "status-color-inactive"
                                                            }
                                                        ></div>
                                                        <div>{order.status}</div>
                                                    </div>
                                                </td>
                                                <td>{t(`enums.OrderType.${order.type}`)}</td>
                                                <td>
                                                    <div className="table-number-format-value">
                                                        {formatDate(order.createdAt)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-number-format-value">
                                                        {formatAmount(order.total, order.currencyId)}
                                                    </div>
                                                </td>
                                                <td>{order.employeeName}</td>
                                                <td>
                                                    <div className="table-number-format-value">
                                                        {formatAmount(order.paidByWallet, order.currencyId)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-number-format-value">
                                                        {formatAmount(order.paidByEmployee, order.currencyId)}
                                                    </div>
                                                </td>

                                                <td>
                                                    <DetailsBtn
                                                        className="basic-button"
                                                        onClick={() => navigateToOrderDetails(order)}
                                                    >
                                                        {t("orders.button.details")}
                                                    </DetailsBtn>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            ""
                        )}
                    </div>
                </div>
            ) : (
                ""
            )}
        </CacheProvider>
    );
};

export default AccountingDocsInvoice;
